import {
  makeAutoObservable,
  observable,
  reaction,
  runInAction,
  when,
} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {
  MembershipApiModel,
  MembershipExpandedCommon,
} from '../../../../api/membership';
import type {CollectionStore} from '../../../../api/utils/collectionStore';
import type {
  Destructible,
  WithBusyManager,
  WithReactionDispose,
} from '../../../../utils/store';
import type {Nullable} from '../../../../utils/utility-types';
import type {MembershipsListParams} from '../../../library/stores/ContractMaterials/type';
import type {CoursesGroupContract, StatusToRender} from '../../types';

import {
  courseExpandCommon,
  type CourseExpandedCommon,
} from '../../../../api/course';
import {
  membershipExpandCommon,
  membershipStore,
} from '../../../../api/membership';
import {createCollectionStore} from '../../../../api/utils/collectionStore';
import {courseFetchClient} from '../../../../services/entity-action/course/course.fetch-client';
import {courseFetchList} from '../../../../services/entity-action/course/course.fetch-list';
import {isCourseAvailableToJoin} from '../../../../utils/entity/course/isCourseAvailableToJoin';
import {envManager} from '../../../../utils/env';
import {
  emitter,
  WS_RECEIVE_MESSAGE_EVENT,
} from '../../../../utils/eventEmitter';
import {logger} from '../../../../utils/logger';
import {
  AsyncBusyManager,
  ReactionDisposeManager,
} from '../../../../utils/store';

type Config = {
  coachId: string;
  disableAutoFetch?: boolean;
  isIndividual?: boolean;
  programId: string;
};

type CourseGroupId = 'archived' | 'available_to_join' | 'invited' | 'joined';

type ReasonKey = 'fetch';

const GROUP_IDS_ORDER: CourseGroupId[] = [
  'invited',
  'joined',
  'available_to_join',
];

const ARCHIVED_MEMBERSHIPS_LIMIT = 20;

export class ClientCoursesStore
  implements Destructible, WithBusyManager<ReasonKey>, WithReactionDispose
{
  activeCourses: CourseExpandedCommon[] = [];
  busy = new AsyncBusyManager<ReasonKey>();
  disposer = new ReactionDisposeManager();
  fetchError = '';
  isReady = false;
  private readonly archivedMembershipStore: CollectionStore<MembershipExpandedCommon>;

  constructor(private readonly config: Config) {
    this.archivedMembershipStore = createCollectionStore<
      MembershipExpandedCommon,
      MembershipsListParams
    >({
      method: 'client.memberships.list',
      params: {
        expand: membershipExpandCommon,
        limit: ARCHIVED_MEMBERSHIPS_LIMIT,
        query: [
          ['program_id', '==', this.config.programId],
          ['status', '==', 'archived'],
        ],
        sort: [['created', -1]],
      },
    });

    makeAutoObservable(
      this,
      {
        activeCourses: observable.shallow,
      },
      {autoBind: true},
    );

    this.busy.register('fetch');

    this.setupReactions();

    emitter.on(WS_RECEIVE_MESSAGE_EVENT, this.handleWSMessage);

    if (!config.disableAutoFetch) {
      this.init();
    }
  }

  init() {
    this.disposer.watch(
      'fetch',
      when(
        () => membershipStore.fetching.success,
        () => {
          this.fetch();
        },
      ),
    );

    this.isReady = true;
  }

  destruct() {
    emitter.off(WS_RECEIVE_MESSAGE_EVENT, this.handleWSMessage);
    this.disposer.dispose();
  }

  get coursesGroupedByStatus() {
    const coursesByStatus = this.groupCoursesByStatus(this.activeCourses);

    if (this.archivedMembershipStore.items.length > 0) {
      const courses: CourseExpandedCommon[] = [];

      this.archivedMembershipStore.items.forEach(
        ({course}) => course && courses.push(course),
      );

      if (courses.length) {
        coursesByStatus.push({
          courses,
          id: 'archived',
          title: this.labels.archived,
        });
      }
    }

    if (coursesByStatus.length === 1) {
      const firstGroup = coursesByStatus[0]!;
      const shouldShowTitleForSingleGroup =
        firstGroup.id === 'invited' || firstGroup.id === 'archived';

      if (!shouldShowTitleForSingleGroup) {
        firstGroup.title = '';
      }
    }

    return coursesByStatus;
  }

  get hasMore() {
    return this.archivedMembershipStore.hasMore;
  }

  get isEmpty() {
    return this.coursesGroupedByStatus.length === 0;
  }

  get isFetching() {
    return this.busy.getReason('fetch') || membershipStore.fetching.inProgress;
  }

  async fetch(silent?: boolean) {
    if (!silent) {
      this.busy.setReason('fetch', true);
    }

    try {
      const {coachId, isIndividual, programId} = this.config;

      let courses: CourseExpandedCommon[] = [];

      if (isIndividual) {
        const [individualCourses] = await Promise.all([
          courseFetchList.asClient({
            params: {
              expand: courseExpandCommon,
              query: [
                ['program_id', '==', programId],
                [
                  'status',
                  '!in',
                  [
                    'proposed',
                    'requested',
                    envManager.isCore ? 'declined' : null,
                  ].filter(Boolean) as MembershipApiModel['status'][],
                ],
              ],
            },
          }),
          this.archivedMembershipStore.fetch({
            limit: Math.max(
              ARCHIVED_MEMBERSHIPS_LIMIT,
              this.archivedMembershipStore.items.length,
            ),
          }),
        ]);

        courses = individualCourses as CourseExpandedCommon[];
      } else {
        const [groupCourses] = await Promise.all([
          courseFetchClient({
            coachId,
            programId,
          }),
          this.archivedMembershipStore.fetch({
            limit: Math.max(
              ARCHIVED_MEMBERSHIPS_LIMIT,
              this.archivedMembershipStore.items.length,
            ),
          }),
        ]);

        courses = groupCourses;
      }

      runInAction(() => {
        this.activeCourses = courses;
      });
    } catch (e) {
      this.fetchError = e.message;
    } finally {
      this.busy.setReason('fetch', false);
    }
  }

  async fetchMore() {
    if (this.archivedMembershipStore.hasMore) {
      try {
        await this.archivedMembershipStore.fetch(
          {offset: this.archivedMembershipStore.items.length},
          {silent: true},
        );
      } catch (e) {
        logger.error(e);
      }
    }
  }

  getCourseStatusToRender(
    course: CourseExpandedCommon,
  ): Nullable<StatusToRender> {
    const membership = membershipStore.membershipLookup.get(course._id);

    const joinedStatuses: MembershipExpandedCommon['status'][] = [
      'accepted',
      'active',
      'frozen',
    ];

    if (membership && joinedStatuses.includes(membership.status)) {
      return 'joined';
    }

    if (membership?.status === 'proposed') {
      return 'invited';
    }

    if (membership?.status === 'requested' || isCourseAvailableToJoin(course)) {
      return 'available_to_join';
    }

    return null;
  }

  private get labels() {
    return {
      archived: i18n.t('shared:archived'),
      available_to_join: '',
      invited: i18n.t('shared:you_are_invited_to_join'),
      joined: i18n.t('shared:active'),
    };
  }

  private groupCoursesByStatus(courses: CourseExpandedCommon[]) {
    const coursesByStatus = new Map<
      CourseGroupId,
      CoursesGroupContract<CourseGroupId>
    >();

    courses.forEach(course => {
      const groupId = this.getCourseStatusToRender(course);

      if (!groupId) {
        return;
      }

      if (!coursesByStatus.has(groupId)) {
        coursesByStatus.set(groupId, {
          courses: [],
          id: groupId,
          title: this.labels[groupId],
        });
      }

      coursesByStatus.get(groupId)?.courses.push(course);
    });

    // We don't show available to join courses if we have courses with invited or joined status
    if (coursesByStatus.has('invited') || coursesByStatus.has('joined')) {
      coursesByStatus.delete('available_to_join');
    }

    return Array.from(coursesByStatus.values()).sort(
      (a, b) => GROUP_IDS_ORDER.indexOf(a.id) - GROUP_IDS_ORDER.indexOf(b.id),
    );
  }

  private handleWSMessage(message) {
    if (message.event.type.includes('membership')) {
      this.fetch(true);
    }
  }

  private setupReactions() {
    this.disposer.watch(
      'invite_decline',
      reaction(
        () => membershipStore.client.declining,
        declining => {
          if (declining.success) {
            this.fetch(true);
          }
        },
      ),
    );
  }
}
